import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus, Search, Microscope } from 'lucide-react';

const AddZooplanktonForm = ({
  isOpen,
  onClose,
  zooplanktonSampleType,
  setZooplanktonSampleType,
  zooplanktonOrganism,
  setZooplanktonOrganism,
  sampleTypeSearchTerm,
  setSampleTypeSearchTerm,
  zooplanktonOrganismSearchTerm,
  setZooplanktonOrganismSearchTerm,
  getFilteredSampleTypes,
  getFilteredZooplanktonOrganisms,
  setNewSampleTypeName,
  setShowAddSampleTypeDialog,
  setNewZooplanktonOrganismName,
  setShowAddZooplanktonOrganismDialog,
  onAddClick
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setZooplanktonSampleType('');
    setZooplanktonOrganism('');
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{pointerEvents: 'none'}}>
      <div 
        className="absolute inset-0 bg-black/50"
        style={{pointerEvents: 'auto'}}
        onClick={handleClose}
      />
      
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 relative z-10" style={{pointerEvents: 'auto'}}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>
        
        <h3 className="text-xl font-bold text-slate-900 mb-6">Legg til ny zooplankton</h3>
        
        <div className="grid grid-cols-1 gap-4">
          {/* Sample type selector with search */}
          <div>
            <Label className="text-sm font-semibold">Prøvetype</Label>
            <div className="flex space-x-2 mt-2">
              <Select 
                value={zooplanktonSampleType}
                onValueChange={(value) => {
                  setZooplanktonSampleType(value);
                  setZooplanktonOrganism('');
                  setZooplanktonOrganismSearchTerm('');
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Velg prøvetype..." />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={sampleTypeSearchTerm}
                        onChange={(e) => setSampleTypeSearchTerm(e.target.value)}
                        placeholder="Søk prøvetyper..."
                        className="pl-9 h-9 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {getFilteredSampleTypes().map((type) => (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center space-x-2">
                        <Microscope className="w-4 h-4 text-teal-600" />
                        <span>{type}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {getFilteredSampleTypes().length === 0 && sampleTypeSearchTerm && (
                    <button
                      onClick={() => {
                        setNewSampleTypeName(sampleTypeSearchTerm);
                        setShowAddSampleTypeDialog(true);
                      }}
                      className="w-full p-3 text-left hover:bg-teal-50 transition-colors border-t"
                    >
                      <div className="flex items-center space-x-2">
                        <Plus className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium">Legg til &quot;{sampleTypeSearchTerm}&quot;</span>
                      </div>
                    </button>
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={() => setShowAddSampleTypeDialog(true)}
                className="px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Organism selector with search */}
          <div>
            <Label className="text-sm font-semibold">Organisme</Label>
            <div className="flex space-x-2 mt-2">
              <Select 
                value={zooplanktonOrganism}
                onValueChange={setZooplanktonOrganism}
                disabled={!zooplanktonSampleType}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={!zooplanktonSampleType ? "Velg prøvetype først..." : "Velg organisme..."} />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={zooplanktonOrganismSearchTerm}
                        onChange={(e) => setZooplanktonOrganismSearchTerm(e.target.value)}
                        placeholder="Søk organismer..."
                        className="pl-9 h-9 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {getFilteredZooplanktonOrganisms().map((org) => (
                    <SelectItem key={org} value={org}>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                        <span>{org}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {getFilteredZooplanktonOrganisms().length === 0 && zooplanktonSampleType && zooplanktonOrganismSearchTerm && (
                    <button
                      onClick={() => {
                        setNewZooplanktonOrganismName(zooplanktonOrganismSearchTerm);
                        setShowAddZooplanktonOrganismDialog(true);
                      }}
                      className="w-full p-3 text-left hover:bg-teal-50 transition-colors border-t"
                    >
                      <div className="flex items-center space-x-2">
                        <Plus className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium">Legg til &quot;{zooplanktonOrganismSearchTerm}&quot;</span>
                      </div>
                    </button>
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={() => setShowAddZooplanktonOrganismDialog(true)}
                disabled={!zooplanktonSampleType}
                className="px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-4">
            <Button
              onClick={handleClose}
              variant="outline"
            >
              Avbryt
            </Button>
            <Button
              onClick={onAddClick}
              className="bg-teal-600 hover:bg-teal-700"
              disabled={!zooplanktonSampleType.trim() || !zooplanktonOrganism.trim()}
            >
              Legg til
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddZooplanktonForm;
